import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ShoppingBag, Loader2, Box, Gift } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Brand } from "../components/Brand";
import { GiftDuckButton } from "../components/GiftDuckButton";
import { API, PRINT_HEIGHTS, DEFAULT_HEIGHT, duckTitle, heroUrl, frontImage, blankOnError, BLANK_IMG } from "../lib/api";

const frontView = (d) => (d?.build_status === "ready" ? heroUrl(d.id) : frontImage(d));

const SizeChoice = ({ s, active, onPick }) => (
  <button
    data-testid={`reorder-size-${s.height}`}
    onClick={() => onPick(s.height)}
    disabled={!s.available}
    className={`rounded-2xl border-2 p-3 text-left transition-colors disabled:opacity-40 ${active ? "border-accent bg-accent/10" : "border-border bg-card hover:border-accent/50"}`}
  >
    <p className="font-heading font-bold text-lg leading-none">{s.height} mm</p>
    <p className="text-sm mt-1">{s.available ? s.price : "sold out"}</p>
  </button>
);

// Tiny landing page for one-tap reorders (emails, share page): pick a size, then the shop's cart opens with this duck attached.
export default function ReorderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [duck, setDuck] = useState(null);
  const [sizes, setSizes] = useState(null);
  const [error, setError] = useState(null);
  const wanted = Number(params.get("height"));
  const [height, setHeight] = useState(PRINT_HEIGHTS.includes(wanted) ? wanted : DEFAULT_HEIGHT);
  const [gift, setGift] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    axios.get(`${API}/share/${id}`).then((r) => setDuck(r.data)).catch((e) => setError(e?.response?.data?.detail || "This duck could not be loaded."));
  }, [id]);

  // Cart links carry the gift note as a line property, so they are re-fetched (debounced) while the note is typed.
  useEffect(() => {
    const t = setTimeout(() => {
      axios
        .get(`${API}/share/${id}/order-sizes`, { params: { colors: params.get("colors") || undefined, note: gift ? note.trim() : "" } })
        .then((s) => setSizes(s.data))
        .catch((e) => setError(e?.response?.data?.detail || "This duck could not be loaded."));
    }, note ? 400 : 0);
    return () => clearTimeout(t);
  }, [id, params, gift, note]);

  const chosen = sizes?.sizes?.find((s) => s.height === height);

  return (
    <div className="App min-h-screen bg-dots">
      <header className="h-20 border-b border-border/70 bg-background/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto h-full px-4 sm:px-8 flex items-center justify-between">
          <Link to="/" data-testid="reorder-logo-link" className="flex items-center gap-3"><Brand /></Link>
          <Link to={`/s/${id}`} data-testid="reorder-share-link" className="text-sm underline text-muted-foreground hover:text-foreground">See it spin</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
        {error ? (
          <p data-testid="reorder-error" className="rounded-2xl border border-border bg-card p-5">{error}</p>
        ) : !duck || !sizes ? (
          <div className="grid place-items-center h-64"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>
        ) : (
          <div data-testid="reorder-card" className="rounded-3xl border-2 border-accent/40 bg-card p-6 sm:p-8 space-y-6 animate-pop">
            <div className="flex gap-5 items-center">
              <img src={frontView(duck) || BLANK_IMG} alt="your duck, painted" onError={blankOnError} className="h-28 w-28 shrink-0 rounded-3xl object-cover border-2 border-background shadow bg-[#f6f4ef]" />
              <div className="min-w-0">
                <span className="text-xs font-mono2 uppercase tracking-widest text-accent font-semibold">Order another print</span>
                <h1 data-testid="reorder-title" className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight mt-1 truncate">{duckTitle(duck) || "Your duck"}</h1>
                <p className="text-sm text-muted-foreground mt-1">Pick a height — {sizes.product || "Custom Duck"} printed and shipped by <span className="font-mono2 text-foreground">{sizes.store}</span>.</p>
              </div>
            </div>
            {sizes.enabled ? (
              <>
                <div data-testid="reorder-sizes" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {sizes.sizes.map((s) => <SizeChoice key={s.height} s={s} active={s.height === height} onPick={setHeight} />)}
                </div>
                <div className="rounded-2xl border border-border bg-background/60 p-4 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input data-testid="reorder-gift-toggle" type="checkbox" checked={gift} onChange={(e) => setGift(e.target.checked)} className="h-5 w-5 accent-[#ffc700]" />
                    <span className="flex items-center gap-2 font-semibold"><Gift className="h-4 w-4 text-accent" /> This is a gift — ship it to someone else</span>
                  </label>
                  {gift && (
                    <>
                      <Textarea
                        data-testid="reorder-gift-note"
                        maxLength={300}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Personal note for the card in the box (optional, 300 characters)"
                        className="rounded-2xl min-h-20"
                      />
                      <p className="text-xs text-muted-foreground">The note travels with the order so the shop can print a card. Enter your friend's address at checkout.</p>
                    </>
                  )}
                </div>
                {sizes.held && (
                  <p data-testid="reorder-held-note" className="rounded-2xl border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-sm">
                    {sizes.held}.
                  </p>
                )}
                <Button asChild disabled={!chosen?.available || !!sizes.held} className="w-full h-14 rounded-full text-base font-bold gap-2 duck-shadow">
                  <a data-testid="reorder-add-to-cart" href={chosen?.url} target="_top" rel="noopener">
                    <ShoppingBag className="h-5 w-5" /> Add to cart · {height} mm{chosen ? ` · ${chosen.price}` : ""}
                  </a>
                </Button>
                <p className="text-xs text-muted-foreground">Opens the shop's cart with this duck's link, size and filament list attached — finish checkout there.</p>
                <div className="flex justify-center"><GiftDuckButton id={id} height={height} className="h-10" /></div>
              </>
            ) : (
              <p data-testid="reorder-disabled" className="rounded-2xl border border-border bg-background p-4 text-sm text-muted-foreground flex items-center gap-2">
                <Box className="h-4 w-4" /> Ordering is not available right now — you can still download and print it from the share page.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
